import React from "react";
import FormInputSelect from "customization/ui/FormInputSelect";

import { asNumber, guessType } from "@rjsf/core/lib/utils";
import { useValidity } from "customization/templates/FieldTemplate/FieldTemplate";

const nums = new Set(["number", "integer"]);

/**
 * This is a silly limitation in the DOM where option change event values are
 * always retrieved as strings.
 */
function processValue(schema, value) {
  // "enum" is a reserved word, so only "type" and "items" can be destructured
  const { type, items } = schema;
  if (value === "") {
    return undefined;
  } else if (type === "array" && items && nums.has(items.type)) {
    return value.map(asNumber);
  } else if (type === "boolean") {
    return value === "true";
  } else if (type === "number") {
    return asNumber(value);
  }

  // If type is undefined, but an enum is present, try and infer the type from
  // the enum values
  if (schema.enum) {
    if (schema.enum.every((x) => guessType(x) === "number")) {
      return asNumber(value);
    } else if (schema.enum.every((x) => guessType(x) === "boolean")) {
      return value === "true";
    }
  }

  return value;
}

/**
 * This is a modifed version of the core widget
 * See https://github.com/mozilla-services/react-jsonschema-form/blob/v1.2.0/src/components/widgets/SelectWidget.js
 */
function SelectWidget(props) {
  const {
    schema,
    id,
    name,
    options,
    value,
    required,
    disabled,
    readonly,
    multiple,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    placeholder,
  } = props;
  const { isInvalid, handleOnBlur } = useValidity();

  const { enumOptions, enumDisabled } = options;
  const emptyValue = "";

  if (multiple) {
    console.warn('"mutiple" is not supported by SelectWidget');
  }

  return (
    <FormInputSelect
      id={id}
      name={name}
      multiple={multiple}
      value={
        typeof value === "undefined"
          ? schema.default
            ? schema.default
            : emptyValue
          : value
      }
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      invalid={isInvalid}
      square
      onBlur={
        onBlur &&
        ((event) => {
          const newValue = event.target.value;
          handleOnBlur();
          onBlur(id, processValue(schema, newValue));
        })
      }
      onFocus={
        onFocus &&
        ((event) => {
          const newValue = event.target.value;
          onFocus(id, processValue(schema, newValue));
        })
      }
      onChange={(event) => {
        const newValue = event.target.value;
        onChange(processValue(schema, newValue));
      }}
    >
      {!multiple && !schema.default && <option value="">{placeholder}</option>}
      {enumOptions.map(({ value, label }, i) => {
        // eslint-disable-next-line
        const disabled = enumDisabled && enumDisabled.indexOf(value) != -1;
        return (
          <FormInputSelect.Option key={i} value={value} disabled={disabled}>
            {label}
          </FormInputSelect.Option>
        );
      })}
    </FormInputSelect>
  );
}

SelectWidget.defaultProps = {
  autofocus: false,
};

export default SelectWidget;
